import { getCookie, setCookie } from 'cookies-next';

const UTM_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

const UTM_DEFAULTS: Record<(typeof UTM_KEYS)[number], string> = {
  utm_source: 'organic',
  utm_medium: 'none',
  utm_campaign: 'organic',
};

export interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

/**
 * Syncs UTM parameters using a last-touch override model:
 *
 * 1. If all three UTM params are present in the current URL → update cookies
 *    with those values (last-touch override).
 * 2. If they are NOT present in the URL → leave cookies unchanged.
 * 3. Returns the current effective UTM values (from cookies, falling back to
 *    defaults if cookies were never set).
 *
 * Call this once on page load / navigation to keep the cookies fresh.
 */
export function syncAndGetUtmParams(): UtmParams {
  if (typeof window === 'undefined') {
    return { ...UTM_DEFAULTS };
  }

  const urlParams = new URLSearchParams(window.location.search);

  const urlUtmSource = urlParams.get('utm_source');
  const urlUtmMedium = urlParams.get('utm_medium');
  const urlUtmCampaign = urlParams.get('utm_campaign');

  // Last-touch override: update cookies only when URL carries UTM params
  if (urlUtmSource || urlUtmMedium || urlUtmCampaign) {
    const valuesToStore: UtmParams = {
      utm_source: urlUtmSource || UTM_DEFAULTS.utm_source,
      utm_medium: urlUtmMedium || UTM_DEFAULTS.utm_medium,
      utm_campaign: urlUtmCampaign || UTM_DEFAULTS.utm_campaign,
    };

    UTM_KEYS.forEach(key => {
      setCookie(key, valuesToStore[key], { maxAge: UTM_COOKIE_MAX_AGE });
    });

    return valuesToStore;
  }

  // URL has no UTM params → read from existing cookies, fallback to defaults
  const result: UtmParams = {
    utm_source: UTM_DEFAULTS.utm_source,
    utm_medium: UTM_DEFAULTS.utm_medium,
    utm_campaign: UTM_DEFAULTS.utm_campaign,
  };

  UTM_KEYS.forEach(key => {
    const cookieVal = getCookie(key);
    if (cookieVal && typeof cookieVal === 'string') {
      result[key] = cookieVal;
    }
  });

  return result;
}
